import { useMixedInventoryStore, rehydrateMixedInventoryStore } from './mixedInventoryStore';
import { SessionDataMap } from '../../organisms-mgp/TabList/TabList.schema';
import { getWindowSession, transformResponse } from '../../utils';

type NumericLike = number | string | null | undefined;

/**
 * Safely converts a potentially nullable/session-derived numeric value.
 *
 * @param value Raw value from session payload.
 * @param fallback Value returned when conversion fails.
 * @returns A finite number.
 */
const toNumber = (value: NumericLike, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/**
 * Normalizes children ages from session formats used by upstream flows.
 *
 * @param childrenAges Comma-delimited string or number array.
 * @returns Valid numeric ages only.
 */
const parseChildrenAges = (childrenAges?: string | number[] | null): number[] => {
  if (Array.isArray(childrenAges)) {
    return childrenAges.filter(age => Number.isFinite(age));
  }

  if (typeof childrenAges === 'string' && childrenAges.length > 0) {
    return childrenAges
      .split(',')
      .map(age => Number(age))
      .filter(age => !Number.isNaN(age));
  }

  return [];
};

/**
 * Initializes the Mixed Inventory store from session data.
 *
 * Rehydrates persisted state first so a valid existing cart is not overwritten
 * on page refresh in SSR/Next.js client hydration scenarios.
 *
 * @returns
 * - true when store is already initialized or initialization succeeds.
 * - false when required booking fields are missing/invalid.
 */
export const initializeMixedInventoryStore = async (): Promise<boolean> => {
  // Load persisted MI state first to preserve in-progress booking/cart data.
  await rehydrateMixedInventoryStore();

  const sessionObject = getWindowSession();
  const { propertyId, checkInDate, checkOutDate, numRooms, numGuestsPerRoom, numChildrenPerRoom, childrenAges } =
    transformResponse(sessionObject, SessionDataMap);

  // Hard stop when the minimum booking context is not available from session.
  if (!propertyId || !checkInDate || !checkOutDate || !numRooms) {
    console.log('Missing required booking fields in session data, skipping MI store initialization.');
    return false;
  }

  const totalRooms = toNumber(numRooms);
  // Prevent creating invalid roomSearchCriteria with non-positive room counts.
  if (totalRooms <= 0) {
    console.log('Invalid totalRooms value in session data, skipping MI store initialization.');
    return false;
  }

  const store = useMixedInventoryStore.getState();

  // If a persisted cart+rooms already exists, reuse it and skip re-seeding from session.
  const isCartExist = Boolean(store?.cartState?.cartId && store?.cartState?.rooms?.length);

  if (isCartExist) {
    // Avoid overwriting existing room/cart selections on refresh or remount.
    console.log('Existing cart found in MI store so skipping re-seeding from session data.');
    return true;
  }

  // Defaults keep store deterministic when upstream session values are absent.
  const adultsPerRoom = toNumber(numGuestsPerRoom, 2);
  const childrenPerRoom = toNumber(numChildrenPerRoom, 0);
  const childAges = parseChildrenAges(childrenAges);

  // Seed the MI booking state from normalized session values.
  store.initializeBooking({
    propertyId,
    checkInDate,
    checkOutDate,
    totalRooms,
    roomSearchCriteria: Array.from({ length: totalRooms }, (_, index) => ({
      roomIndex: index + 1,
      adults: adultsPerRoom,
      children: childrenPerRoom,
      childrenAges: childAges,
    })),
  });

  return true;
};
